import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../features/auth/authSlice';
import {
  ISO_IMPLEMENTATION_META,
  type IsoImplementationStandard,
} from '../features/iso9001/implementation/isoImplementationConstants';
import { fetchImplementationGuideFull } from '../features/iso9001/implementation/isoImplementationApi';
import { IsoImplementationUnavailable } from '../features/iso9001/implementation/IsoImplementationUnavailable';
import { getApiErrorMessage } from '../features/iso9001/implementation/isoImplementationError';
import type { ImplementationGuideDetail, TaskWithProgressView } from '../features/iso9001/implementation/types';
import {
  IsoGuideOrgContextPanel,
  IsoGuideRequirementCard,
} from '../features/iso9001/implementation/IsoGuideComponents';
import { useGuideContextUpdater } from '../features/iso9001/implementation/useGuideContextUpdater';

const PLATFORM_LINKS_BY_STANDARD: Partial<
  Record<IsoImplementationStandard, Array<{ label: string; platformRoute: string; description?: string }>>
> = {
  iso9001: [
    { label: 'Contexto organizacional', platformRoute: '/contexto', description: 'Cláusulas 4.1–4.2' },
    { label: 'Riesgos y oportunidades', platformRoute: '/riesgos', description: 'Cláusula 6.1' },
    { label: 'Objetivos de calidad', platformRoute: '/objetivos', description: 'Cláusula 6.2' },
    { label: 'Requisitos legales', platformRoute: '/requisitos-legales', description: 'Cláusula 4.2 / cumplimiento' },
    { label: 'Competencias', platformRoute: '/competencias', description: 'Cláusula 7.2' },
    { label: 'Documentos del SGC', platformRoute: '/documentos', description: 'Cláusula 7.5' },
    { label: 'Matriz de comunicación', platformRoute: '/communication-matrix', description: 'Cláusula 7.4' },
    { label: 'Proveedores', platformRoute: '/proveedores', description: 'Cláusula 8.4' },
    { label: 'Diseño y desarrollo', platformRoute: '/iso9001/diseno', description: 'Cláusula 8.3' },
    { label: 'Salidas no conformes', platformRoute: '/iso9001/salidas-no-conformes', description: 'Cláusula 8.7' },
    { label: 'No conformidades (CAPA)', platformRoute: '/registros/no-conformidades', description: 'Cláusula 10.2' },
    { label: 'Auditorías internas', platformRoute: '/auditorias', description: 'Cláusula 9.2' },
    { label: 'Revisión por la dirección', platformRoute: '/revision-direccion', description: 'Cláusula 9.3' },
    { label: 'Gestión del cambio', platformRoute: '/gestion-cambios', description: 'Cláusula 6.3' },
  ],
  iso27001: [
    { label: 'Activos de información', platformRoute: '/iso27001/activos', description: 'A.5.9' },
    { label: 'Riesgos de seguridad', platformRoute: '/riesgos', description: 'Cláusula 6.1' },
    { label: 'Declaración de aplicabilidad', platformRoute: '/iso27001/soa', description: 'Anexo A' },
    { label: 'Incidentes de seguridad', platformRoute: '/iso27001/incidentes', description: 'A.5.24–A.5.28' },
    { label: 'No conformidades (CAPA)', platformRoute: '/registros/no-conformidades', description: 'Cláusula 10.1' },
    { label: 'Bitácora de seguridad', platformRoute: '/iso27001/bitacora', description: 'A.8.15' },
    { label: 'Política de contraseñas', platformRoute: '/iso27001/politica-contrasenas', description: 'A.5.17' },
  ],
  iso45001: [
    { label: 'Contexto organizacional', platformRoute: '/contexto', description: 'Cláusulas 4.1–4.2' },
    { label: 'Riesgos y peligros SST', platformRoute: '/riesgos', description: 'Cláusula 6.1' },
    { label: 'Requisitos legales SST', platformRoute: '/requisitos-legales', description: 'Cláusula 6.1.3' },
    { label: 'Objetivos de SST', platformRoute: '/objetivos', description: 'Cláusula 6.2' },
    { label: 'Incidentes SST', platformRoute: '/iso45001/incidentes', description: 'Cláusula 10.2' },
    { label: 'Consulta de trabajadores', platformRoute: '/iso45001/consulta', description: 'Cláusula 5.4' },
    { label: 'No conformidades (CAPA)', platformRoute: '/registros/no-conformidades', description: 'Cláusula 10.2' },
    { label: 'Auditorías internas', platformRoute: '/auditorias', description: 'Cláusula 9.2' },
    { label: 'Revisión por la dirección', platformRoute: '/revision-direccion', description: 'Cláusula 9.3' },
    { label: 'Emergencias', platformRoute: '/emergencias', description: 'Cláusula 8.2' },
  ],
};

interface IsoGuideNativePageProps {
  standard: IsoImplementationStandard;
}

export function IsoGuideNativePage({ standard }: IsoGuideNativePageProps) {
  const meta = ISO_IMPLEMENTATION_META[standard];
  const user = useSelector(selectCurrentUser);
  const isSuperadmin = user?.role === 'superadmin';
  const platformLinks = PLATFORM_LINKS_BY_STANDARD[standard] ?? [];

  const [guide, setGuide] = useState<ImplementationGuideDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeClauseId, setActiveClauseId] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  const updateContext = useGuideContextUpdater(setGuide);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchImplementationGuideFull('guia', standard);
      setGuide(data);
      if (data.phases[0]) {
        setActiveClauseId(data.phases[0].id);
        const firstTask = data.phases[0].tasks[0];
        if (firstTask) setSelectedTaskId(firstTask.id);
      }
    } catch (err) {
      setError(getApiErrorMessage(err) ?? 'No se pudo cargar la guía de implementación.');
    } finally {
      setLoading(false);
    }
  }, [standard]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedTask: TaskWithProgressView | null = useMemo(() => {
    if (!guide || !selectedTaskId) return null;
    for (const phase of guide.phases) {
      const found = phase.tasks.find((t) => t.id === selectedTaskId);
      if (found) return found;
    }
    return null;
  }, [guide, selectedTaskId]);

  const activePhase = guide?.phases.find((p) => p.id === activeClauseId) ?? guide?.phases[0];

  if (loading) {
    return <p className="p-6 text-sm text-muted-foreground">Cargando guía…</p>;
  }

  if (error || !guide) {
    return (
      <IsoImplementationUnavailable
        standard={standard}
        guideSlug="guia"
        title="No se pudo cargar la guía de implementación."
        detail={error}
      />
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col lg:flex-row">
      <nav className="w-full shrink-0 border-b border-border bg-card p-4 lg:w-64 lg:border-b-0 lg:border-r lg:overflow-y-auto">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {meta.label} · Guía
        </p>
        <h1 className="mt-1 text-lg font-semibold leading-tight">{guide.title}</h1>
        <Link
          to={meta.progressPath}
          className="mt-2 inline-block text-sm text-primary underline-offset-4 hover:underline"
        >
          ← Volver al avance
        </Link>

        <div className="mt-6 space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Cláusulas
          </p>
          <ul className="space-y-0.5">
            {guide.phases.map((phase) => (
              <li key={phase.id}>
                <button
                  type="button"
                  className={`w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted ${
                    activeClauseId === phase.id ? 'bg-muted font-medium' : ''
                  }`}
                  onClick={() => {
                    setActiveClauseId(phase.id);
                    const first = phase.tasks[0];
                    if (first) setSelectedTaskId(first.id);
                    document.getElementById(`clause-${phase.id}`)?.scrollIntoView({
                      behavior: 'smooth',
                    });
                  }}
                >
                  {phase.eyebrow ?? phase.title}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {platformLinks.length > 0 ? (
          <div className="mt-6 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Módulos de la plataforma
            </p>
            <ul className="space-y-1">
              {platformLinks.map((link) => (
                <li key={link.platformRoute}>
                  <Link
                    to={link.platformRoute}
                    className="block rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                  >
                    <span className="font-medium">{link.label}</span>
                    {link.description ? (
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {link.description}
                      </span>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </nav>

      <main className="min-w-0 flex-1 overflow-y-auto p-4 md:p-6 lg:max-w-3xl">
        {activePhase ? (
          <section id={`clause-${activePhase.id}`} className="mb-8 scroll-mt-4">
            <h2 className="border-b-2 border-foreground pb-2 text-xl font-semibold">
              {activePhase.title}
            </h2>
            {activePhase.description ? (
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {activePhase.description}
              </p>
            ) : null}
            <div className="mt-6 space-y-3">
              {activePhase.tasks.map((task) => (
                <IsoGuideRequirementCard
                  key={task.id}
                  task={task}
                  isSelected={selectedTaskId === task.id}
                  onSelect={() => setSelectedTaskId(task.id)}
                />
              ))}
            </div>
          </section>
        ) : null}
      </main>

      <div className="w-full shrink-0 border-t border-border bg-card p-4 lg:w-80 lg:border-l lg:border-t-0 lg:overflow-y-auto">
        {selectedTask ? (
          <IsoGuideOrgContextPanel
            task={selectedTask}
            isSuperadmin={isSuperadmin}
            onUpdated={(ctx) => updateContext(selectedTask.id, ctx)}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            Seleccioná un requisito para ver el contexto de la empresa.
          </p>
        )}
      </div>
    </div>
  );
}
